import { action, computed, flow, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import { LOCALES } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { ICountry } from 'src/types/country';
import { LoadingStatus, paginationCast, TBooleanInNumber, TPagination } from 'src/types/main';
import { defaultCountriesFilters, defaultCountry, defaultCountryValidation } from 'src/view/screens/SettingsScreen/constants/countries';
import { ICountriesFilters, ICountryValidation } from 'src/view/screens/SettingsScreen/screens/General/LanguageCountryCurrency/types/countries';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

class CountriesStore {
  constructor() {
    makeObservable(this);
  }

  getCountriesAbortController = new AbortController();
  countriesGetTimeoutId: NodeJS.Timeout | null = setTimeout(() => {
    return;
  }, 0);

  @observable public countriesLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public countryLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public countryCreateLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public countryUpdateLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public countryDeleteLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @persist @observable countries: ICountry[] = [];
  @persist @observable public pagination: TPagination = { currentPage: 0, lastPage: 0, totalRows: 0, perPage: 0 };
  @persist @observable country: ICountry | null = null;
  @observable filters: ICountriesFilters = defaultCountriesFilters;
  @observable edited: boolean = false;
  @observable originIsDefault: TBooleanInNumber = 0;
  @observable validation: ICountryValidation = defaultCountryValidation;

  @action setFilters = (filters: Partial<ICountriesFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this._getCountriesWithDelay();
  };

  @computed get isValid(): boolean {
    return (
      this.validation.name[LOCALES.EN].state &&
      this.validation.name[LOCALES.AR].state &&
      this.validation.isd_code.state &&
      this.validation.alfa_2_code.state &&
      this.validation.numeric_code.state &&
      this.validation.place_id.state
    );
  }

  private _getCountries = flow(function* (this: CountriesStore, pageNumber: number = 1): any {
    this.getCountriesAbortController.abort();
    this.getCountriesAbortController = getAbortController();

    try {
      this.countriesLoadingStatus = LoadingStatus.Loading;

      const params: any = {
        page: pageNumber,
        name: this.filters.search || '',
        per_page: this.filters.perPage || undefined,
        sort_field: this.filters.sortField || undefined,
        sort_order: this.filters.sortDir || undefined,
      };

      const response = yield client.get('v3/countries', {
        params,
        headers: { 'Accept-Language': 'en,ar' },
        signal: this.getCountriesAbortController.signal,
      });

      this.countries = this.sortCountriesList(response.data);
      this.pagination = paginationCast(response);
      this.countriesLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.countriesLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.countriesLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'CountriesStore _getCountries', showModal: true });
    }
  }).bind(this);

  @action getCountry = flow(function* (this: CountriesStore, countryId: number | string): any {
    try {
      this.countryLoadingStatus = LoadingStatus.Loading;

      const response = yield client.get(`v3/countries/${countryId}`, { headers: { 'Accept-Language': 'en,ar' } });

      this.setCountry(response);
      this.countryLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.countryLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'CountriesStore getCountry', showModal: true });
    }
  }).bind(this);

  @action getCountries = () => {
    if (this.countriesLoadingStatus === LoadingStatus.Init) {
      this._getCountriesWithNoDelay();
    } else {
      this._getCountriesWithDelay();
    }
  };

  @action private _getCountriesWithNoDelay = (pageNumber: number = 1) => {
    if (this.countriesGetTimeoutId) {
      clearTimeout(this.countriesGetTimeoutId);
    }
    this._getCountries(pageNumber);
  };

  @action private _getCountriesWithDelay = (pageNumber: number = 1) => {
    if (this.countriesGetTimeoutId) {
      clearTimeout(this.countriesGetTimeoutId);
    }
    this.countriesGetTimeoutId = setTimeout(() => {
      this._getCountries(pageNumber);
    }, 700);
  };

  @action public changePage = (pageNumber: number) => {
    this._getCountriesWithNoDelay(pageNumber);
  };

  private sortCountriesList(countries: ICountry[]) {
    return countries.sort((first, second) => {
      if (first.is_default) {
        return -1;
      }
      if (second.is_default) {
        return 1;
      }
      return 0;
    });
  }

  @action public setCountry = (country?: ICountry) => {
    this.country = country ? country : defaultCountry;
    this.originIsDefault = this.country.is_default;
    this.countryLoadingStatus = LoadingStatus.Success;
    this.countryDeleteLoadingStatus = LoadingStatus.Init;
    this.countryCreateLoadingStatus = LoadingStatus.Init;
    this.countryUpdateLoadingStatus = LoadingStatus.Init;
    this.validation = defaultCountryValidation;
  };

  private clearValidationKey(key: string) {
    if (!Object.keys(this.validation).includes(key)) {
      return;
    }
    if (key === 'name') {
      this.validation = {
        ...this.validation,
        name: {
          [LOCALES.EN]: {
            state: true,
            errorText: undefined,
          },
          [LOCALES.AR]: {
            state: true,
            errorText: undefined,
          },
        },
      };
      return;
    }
    this.validation = {
      ...this.validation,
      [key]: {
        state: true,
        errorText: undefined,
      } as Partial<ICountryValidation>,
    };
  }

  @action public changeCountryHandler = (value: Partial<ICountry>) => {
    if (!this.country) return;
    const key = Object.keys(value)[0];
    this.clearValidationKey(key);
    this.country = { ...this.country, ...value };
    this.edited = true;
  };

  @action public deleteCountry = async (countryId: number) => {
    try {
      this.countriesLoadingStatus = LoadingStatus.Refreshing;
      this.countryDeleteLoadingStatus = LoadingStatus.Loading;

      await client.delete(`v3/countries/${countryId}`);

      if (this.pagination.currentPage > 0) {
        this._getCountries(this.pagination.currentPage);
      }

      this.countryDeleteLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.countryDeleteLoadingStatus = LoadingStatus.Error;
      this.countriesLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'CountriesStore deleteCountry', showModal: true });
    }
  };

  @action public setAsDefault = async (countryId: number) => {
    const country = this.countries.find((_country) => _country.id === countryId);
    if (!country) {
      return;
    }
    this.countriesLoadingStatus = LoadingStatus.Refreshing;
    this.country = {
      ...country,
      is_default: 1,
    };
    await this.updateCountry();
    this.country = null;
    this._getCountries(this.pagination.currentPage);
  };

  @action public createCountry = async () => {
    if (!this.country) return;

    try {
      this.countryCreateLoadingStatus = LoadingStatus.Loading;

      const response = await client.post('v3/countries', { ...this.country }, { headers: { 'Accept-Language': 'en,ar' } });

      this.country.id = response.id;
      this.edited = false;
      this.countryCreateLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.countryCreateLoadingStatus = LoadingStatus.Error;

      if (this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'CountriesStore createCountry', showModal: true });
    }
  };

  @action public updateCountry = async () => {
    if (!this.country?.id) return;

    try {
      this.countryLoadingStatus = LoadingStatus.Loading;
      this.countryUpdateLoadingStatus = LoadingStatus.Loading;

      await client.put(`v3/countries/${this.country?.id}`, { ...this.country }, { headers: { 'Accept-Language': 'en,ar' } });

      this.edited = false;
      this.countryLoadingStatus = LoadingStatus.Success;
      this.countryUpdateLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.countryLoadingStatus = LoadingStatus.Error;
      this.countryUpdateLoadingStatus = LoadingStatus.Error;

      if (this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'CountriesStore createCountry', showModal: true });
    }
  };

  private isErrorResponseHandled(error: any): boolean {
    const errorsBag = error.response.data;
    let hasApiErrors = false;
    if (errorsBag['name.ar']) {
      this.validation.name[LOCALES.AR].errorText = errorsBag['name.ar'][0];
      this.validation.name[LOCALES.AR].state = false;
      hasApiErrors = true;
    }
    if (errorsBag['name.en']) {
      this.validation.name[LOCALES.EN].errorText = errorsBag['name.en'][0];
      this.validation.name[LOCALES.EN].state = false;
      hasApiErrors = true;
    }
    if (errorsBag.numeric_code) {
      this.validation.numeric_code.errorText = errorsBag.numeric_code[0];
      this.validation.numeric_code.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.alfa_2_code) {
      this.validation.alfa_2_code.errorText = errorsBag.alfa_2_code[0];
      this.validation.alfa_2_code.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.isd_code) {
      this.validation.isd_code.errorText = errorsBag.isd_code[0];
      this.validation.isd_code.state = false;
      hasApiErrors = true;
    }
    return hasApiErrors;
  }

  @action unMount = () => {
    this.country = null;
    this.edited = false;

    if (!window.location.pathname.includes(SCREENS.SETTINGS_COUNTRIES())) this.filters = defaultCountriesFilters;
  };
}

const countriesStore = new CountriesStore();
export default countriesStore;
